import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Paper, Typography, useMediaQuery} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {useTranslation} from "react-i18next";
import classnames from "classnames";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";

/**
 * AP aged account summary for a vendor (CleanAF AccountSummary).
 */
const VendorAccountSummarySection = ({vendorId, loading: loadingVendor}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.accountSummary'});
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const isMounted = useIsMounted();
    const shouldSplitInTwo = useMediaQuery((theme) => theme.breakpoints.up('xl'));

    /**
     * Loads AP account summary for the scoped vendor.
     */
    const loadSummary = useCallback(async () => {
        if (!vendorId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await Api.getVendorAccountSummary(Number(vendorId));
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSummary(response.data ?? null);
        } else {
            setSummary(null);
        }
        setLoading(false);
    }, [isMounted, vendorId]);

    useEffect(() => {
        void loadSummary();
    }, [loadSummary]);

    const accountSummaryData = useMemo(() => {
        const data = summary ?? {};
        const withMoney = (id, title, value) => ({
            id,
            title,
            value: value ?? 0,
            format: formatMoney,
        });
        return {
            body1: [
                withMoney('future', t('future'), data.future),
                withMoney('current', t('current'), data.current),
                withMoney('period0', t('period0'), data.period0),
                withMoney('period1', t('period1'), data.period1),
            ],
            body2: [
                withMoney('period2', t('period2'), data.period2),
                withMoney('period3', t('period3'), data.period3),
                withMoney('total', t('total'), data.totalBalance ?? data.total),
            ],
        };
    }, [summary, t]);

    const [body1Info, body2Info] = useMemo(() => {
        let body1 = accountSummaryData.body1;
        let body2 = accountSummaryData.body2;
        if (!shouldSplitInTwo || !body2.length) {
            body1 = [...body1, ...body2];
            body2 = null;
        }
        return [body1, body2];
    }, [accountSummaryData.body1, accountSummaryData.body2, shouldSplitInTwo]);

    const busy = loadingVendor || loading;

    return (
        <Paper sx={{p: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%'}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    {t('title')}
                </Typography>
            </Box>
            <Grid2 container spacing={2}>
                <Grid2 xs={12} xl={!body2Info ? 12 : 6}>
                    <SummaryInfoCard
                        className={classnames({'pb-0': !!body2Info})}
                        body={body1Info}
                        loading={busy}
                        loadingCount={body1Info.length}
                    />
                </Grid2>
                {
                    !!body2Info &&
                    <Grid2 xs={12} xl={6}>
                        <SummaryInfoCard
                            className={'pt-0'}
                            body={body2Info}
                            loading={busy}
                            loadingCount={body2Info.length}
                        />
                    </Grid2>
                }
            </Grid2>
        </Paper>
    );
};

export default VendorAccountSummarySection;
